/**
 * Record-a-trailer — standalone PAGE (V2 architecture, 2026-05-10).
 *
 * Replaces the previous modal-overlay approach. The recording surface
 * is now its own browser tab opened via window.open('#/record-trailer'),
 * not a dialog inside the app. Why:
 *
 *   - When the rep screen-shares, they don't risk capturing TrailerCast's
 *     own UI in the recording (the modal-on-top approach kept dragging
 *     the toolbar into the captured frame).
 *   - Multi-monitor users can drag the entire tab to another display.
 *   - Cleaner mental model — "I'm in recording mode" is a different
 *     surface, not a modal layered on the main app.
 *   - Kills the popout-window juggling, the z-index containing-block
 *     bugs, and the white-screen-after-stop class of issues at once.
 *
 * Inter-tab communication: BroadcastChannel('tc-recordings'). After a
 * successful save, this tab posts { type: 'saved', demo } and closes
 * itself. The parent tab listens and refreshes its trailer library.
 *
 * Auth: reads the token from localStorage where the parent put it on
 * login. Same key the rest of the SPA uses ('trailercst_token').
 *
 * Recording mechanics (preserved from the previous modal):
 *   - getDisplayMedia + getUserMedia
 *   - MediaRecorder for the encoded blob
 *   - Canvas compositing for PIP, repainted by a Web Worker timer so
 *     it doesn't get tab-visibility-throttled (workers don't slow
 *     down when the tab is backgrounded)
 *   - 3-2-1 countdown before MediaRecorder.start() fires
 *   - 10-min soft warning, 15-min hard auto-stop
 *
 * V2 phase 2 (next session): swap canvas-based PIP for dual-stream
 * recording (camera + screen as separate files, server-side FFmpeg
 * compose). Kills the canvas-throttling class of bugs entirely.
 */

import { useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';
const BROADCAST_CHANNEL = 'tc-recordings';

// Length caps. 15 min is plenty for a sales walkthrough; longer is
// almost certainly a misuse case (use Upload instead).
const SOFT_WARN_SECONDS = 10 * 60;
const HARD_CAP_SECONDS = 15 * 60;

// PIP composite dimensions. 1920x1080 at 30fps for crisp playback.
const COMPOSITE_WIDTH = 1920;
const COMPOSITE_HEIGHT = 1080;
const PIP_DIAMETER = 280;
const PIP_MARGIN = 32;
const COMPOSITE_FPS = 30;

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function deriveLayout({ hasCamera, hasScreen }) {
  if (hasCamera && hasScreen) return 'screen_camera_pip';
  if (hasScreen) return 'screen';
  if (hasCamera) return 'camera';
  return null;
}

export default function RecordTrailerPage() {
  // Auth pulled from localStorage where the parent tab wrote it on login.
  const [authToken] = useState(() => {
    try { return localStorage.getItem('trailercst_token') || ''; } catch (_) { return ''; }
  });

  // ─── State machine ─────────────────────────────────────────────
  // 'setup'     → live previews, configure mic/cam/screen, hit Record
  // 'countdown' → 3-2-1 overlay; recorder hasn't started yet
  // 'recording' → capture in progress
  // 'preview'   → review, save, or discard
  // 'saved'     → upload complete; show "All set, you can close this tab"
  const [step, setStep] = useState('setup');
  const [countdown, setCountdown] = useState(0);

  // Source toggles.
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);

  // Stream refs.
  const cameraStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const compositeStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const drawWorkerRef = useRef(null);

  // Hidden composite video elements (PIP only).
  const screenVideoElRef = useRef(null);
  const cameraVideoElRef = useRef(null);

  // Live preview videos in the page.
  const setupCameraPreviewRef = useRef(null);
  const setupScreenPreviewRef = useRef(null);

  // Recording timing.
  const startedAtRef = useRef(0);
  const tickIntervalRef = useRef(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showSoftWarn, setShowSoftWarn] = useState(false);

  // Result.
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [savedDemo, setSavedDemo] = useState(null);

  // Errors + acquisition flags.
  const [error, setError] = useState(null);
  const [acquiringCamera, setAcquiringCamera] = useState(false);
  const [acquiringScreen, setAcquiringScreen] = useState(false);

  // ─── Mount + cleanup ──────────────────────────────────────────

  useEffect(() => {
    document.title = 'Record a trailer · TrailerCast';
    if (!isIOS()) acquireCamera();
    // Warn user before navigating away mid-recording.
    const warnBeforeUnload = (e) => {
      if (step === 'recording' || (step === 'preview' && recordedBlob)) {
        e.preventDefault();
        e.returnValue = ''; // Chrome shows generic dialog, not our text
      }
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
      stopAllStreams();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (setupCameraPreviewRef.current && cameraStreamRef.current) {
      try {
        setupCameraPreviewRef.current.srcObject = cameraStreamRef.current;
        setupCameraPreviewRef.current.play().catch(() => {});
      } catch (_) {}
    }
  }, [cameraOn, step]);

  useEffect(() => {
    if (setupScreenPreviewRef.current && screenStreamRef.current) {
      try {
        setupScreenPreviewRef.current.srcObject = screenStreamRef.current;
        setupScreenPreviewRef.current.play().catch(() => {});
      } catch (_) {}
    }
  }, [screenSharing, step]);

  // ─── Stream acquisition ───────────────────────────────────────

  async function acquireCamera() {
    if (cameraStreamRef.current) return;
    setAcquiringCamera(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, frameRate: 30 },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      cameraStreamRef.current = stream;
      setCameraOn(true);
      stream.getAudioTracks().forEach((t) => { t.enabled = micOn; });
    } catch (err) {
      handleAcquireError(err, 'camera');
    } finally {
      setAcquiringCamera(false);
    }
  }

  function releaseCamera() {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
    setCameraOn(false);
  }

  async function acquireScreen() {
    if (screenStreamRef.current) return;
    setAcquiringScreen(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30 }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      screenStreamRef.current = stream;
      setScreenSharing(true);
      const track = stream.getVideoTracks()[0];
      if (track) {
        track.addEventListener('ended', () => {
          if (screenStreamRef.current === stream) {
            screenStreamRef.current = null;
            setScreenSharing(false);
          }
        });
      }
    } catch (err) {
      handleAcquireError(err, 'screen');
    } finally {
      setAcquiringScreen(false);
    }
  }

  function releaseScreen() {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }
    setScreenSharing(false);
  }

  function handleAcquireError(err, kind) {
    if (err.name === 'NotAllowedError') {
      setError(`${kind === 'screen' ? 'Screen sharing' : 'Camera / microphone'} permission denied. Click the icon in your browser's address bar to grant access, then try again.`);
    } else if (err.name === 'NotFoundError') {
      setError(`No ${kind === 'screen' ? 'screen available to share' : 'camera or microphone found'}.`);
    } else if (err.name !== 'AbortError') {
      setError(err.message || `Could not start ${kind}.`);
    }
  }

  function toggleCamera() { if (cameraOn) releaseCamera(); else acquireCamera(); }
  function toggleMic() {
    const next = !micOn;
    setMicOn(next);
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = next; });
    }
  }
  function toggleScreen() { if (screenSharing) releaseScreen(); else acquireScreen(); }

  // ─── Composite stream ─────────────────────────────────────────

  async function buildCompositeStream() {
    const layout = deriveLayout({ hasCamera: cameraOn, hasScreen: screenSharing });
    if (!layout) return null;

    if (layout === 'screen') {
      const tracks = [...screenStreamRef.current.getVideoTracks()];
      if (micOn && cameraStreamRef.current) {
        tracks.push(...cameraStreamRef.current.getAudioTracks());
      }
      return { stream: new MediaStream(tracks), layout };
    }
    if (layout === 'camera') {
      return { stream: cameraStreamRef.current, layout };
    }

    // PIP composite via canvas.
    const canvas = document.createElement('canvas');
    canvas.width = COMPOSITE_WIDTH;
    canvas.height = COMPOSITE_HEIGHT;
    const ctx = canvas.getContext('2d');

    const screenVid = document.createElement('video');
    screenVid.srcObject = screenStreamRef.current;
    screenVid.muted = true;
    screenVid.playsInline = true;
    screenVid.style.position = 'fixed';
    screenVid.style.left = '-99999px';
    screenVid.style.width = '1px';
    screenVid.style.height = '1px';
    document.body.appendChild(screenVid);
    await screenVid.play();
    screenVideoElRef.current = screenVid;

    const cameraVid = document.createElement('video');
    cameraVid.srcObject = new MediaStream(cameraStreamRef.current.getVideoTracks());
    cameraVid.muted = true;
    cameraVid.playsInline = true;
    cameraVid.style.position = 'fixed';
    cameraVid.style.left = '-99999px';
    cameraVid.style.width = '1px';
    cameraVid.style.height = '1px';
    document.body.appendChild(cameraVid);
    await cameraVid.play();
    cameraVideoElRef.current = cameraVid;

    function drawFrame() {
      try {
        if (screenVid.paused) screenVid.play().catch(() => {});
        if (cameraVid.paused) cameraVid.play().catch(() => {});
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (screenVid.readyState >= 2) {
          const sw = screenVid.videoWidth || 1920;
          const sh = screenVid.videoHeight || 1080;
          const scale = Math.max(canvas.width / sw, canvas.height / sh);
          const dw = sw * scale, dh = sh * scale;
          const dx = (canvas.width - dw) / 2, dy = (canvas.height - dh) / 2;
          ctx.drawImage(screenVid, dx, dy, dw, dh);
        }
        if (cameraVid.readyState >= 2) {
          const cx = canvas.width - PIP_MARGIN - PIP_DIAMETER / 2;
          const cy = canvas.height - PIP_MARGIN - PIP_DIAMETER / 2;
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, PIP_DIAMETER / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          const cw = cameraVid.videoWidth || 720;
          const ch = cameraVid.videoHeight || 720;
          const camSize = Math.min(cw, ch);
          const camSx = (cw - camSize) / 2, camSy = (ch - camSize) / 2;
          ctx.drawImage(cameraVid, camSx, camSy, camSize, camSize,
            cx - PIP_DIAMETER / 2, cy - PIP_DIAMETER / 2, PIP_DIAMETER, PIP_DIAMETER);
          ctx.restore();
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 5;
          ctx.beginPath();
          ctx.arc(cx, cy, PIP_DIAMETER / 2, 0, Math.PI * 2);
          ctx.stroke();
        }
      } catch (_) { /* ignore */ }
    }
    drawFrame();

    // Worker-driven 30Hz tick. Workers aren't tab-throttled.
    const workerSrc = `
      let timer = null;
      self.onmessage = (e) => {
        if (e.data && e.data.type === 'start') {
          if (timer) clearInterval(timer);
          timer = setInterval(() => self.postMessage('tick'), e.data.ms || 33);
        } else if (e.data === 'stop') {
          if (timer) { clearInterval(timer); timer = null; }
        }
      };
    `;
    const blob = new Blob([workerSrc], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);
    worker.onmessage = () => drawFrame();
    worker.postMessage({ type: 'start', ms: Math.round(1000 / COMPOSITE_FPS) });
    drawWorkerRef.current = worker;
    setTimeout(() => { try { URL.revokeObjectURL(workerUrl); } catch (_) {} }, 5000);

    const canvasStream = canvas.captureStream(COMPOSITE_FPS);
    if (micOn && cameraStreamRef.current) {
      cameraStreamRef.current.getAudioTracks().forEach((t) => canvasStream.addTrack(t));
    }
    return { stream: canvasStream, layout };
  }

  // ─── Recording ────────────────────────────────────────────────

  async function startRecording() {
    setError(null);
    const built = await buildCompositeStream();
    if (!built) {
      setError('Turn on camera or share your screen before recording.');
      return;
    }
    compositeStreamRef.current = built.stream;

    const mimeCandidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];
    let chosenMime = '';
    for (const m of mimeCandidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
        chosenMime = m;
        break;
      }
    }
    recordedChunksRef.current = [];
    const recorder = new MediaRecorder(built.stream, chosenMime ? { mimeType: chosenMime } : undefined);
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType || 'video/webm' });
      const finalDuration = Math.round((Date.now() - startedAtRef.current) / 1000);
      setRecordedBlob(blob);
      setRecordedDuration(finalDuration);
      setStep('preview');
    };
    recorderRef.current = recorder;

    runCountdownThenStart(recorder);
  }

  function runCountdownThenStart(recorder) {
    setStep('countdown');
    setCountdown(3);
    let n = 3;
    const cdInterval = setInterval(() => {
      n -= 1;
      if (n > 0) {
        setCountdown(n);
      } else {
        clearInterval(cdInterval);
        setCountdown(0);
        try { recorder.start(1000); } catch (_) {}
        startedAtRef.current = Date.now();
        setElapsedSeconds(0);
        setShowSoftWarn(false);
        tickIntervalRef.current = setInterval(() => {
          const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
          setElapsedSeconds(secs);
          if (secs >= SOFT_WARN_SECONDS && secs < SOFT_WARN_SECONDS + 2) setShowSoftWarn(true);
          if (secs >= HARD_CAP_SECONDS) stopRecording();
        }, 250);
        setStep('recording');
      }
    }, 1000);
  }

  function pauseRecording() {
    try {
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        recorderRef.current.pause();
        setPaused(true);
      }
    } catch (_) {}
  }
  function resumeRecording() {
    try {
      if (recorderRef.current && recorderRef.current.state === 'paused') {
        recorderRef.current.resume();
        setPaused(false);
      }
    } catch (_) {}
  }

  function stopRecording() {
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
    } catch (_) {}
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
    if (drawWorkerRef.current) {
      try { drawWorkerRef.current.postMessage('stop'); } catch (_) {}
      try { drawWorkerRef.current.terminate(); } catch (_) {}
      drawWorkerRef.current = null;
    }
    if (screenVideoElRef.current) {
      try { document.body.removeChild(screenVideoElRef.current); } catch (_) {}
      screenVideoElRef.current = null;
    }
    if (cameraVideoElRef.current) {
      try { document.body.removeChild(cameraVideoElRef.current); } catch (_) {}
      cameraVideoElRef.current = null;
    }
    [screenStreamRef.current, cameraStreamRef.current].forEach((s) => {
      if (s) s.getTracks().forEach((t) => t.stop());
    });
    screenStreamRef.current = null;
    cameraStreamRef.current = null;
    setScreenSharing(false);
    setCameraOn(false);
  }

  function stopAllStreams() {
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
    } catch (_) {}
    [screenStreamRef.current, cameraStreamRef.current, compositeStreamRef.current].forEach((s) => {
      if (s) s.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
    });
    screenStreamRef.current = null;
    cameraStreamRef.current = null;
    compositeStreamRef.current = null;
    if (drawWorkerRef.current) {
      try { drawWorkerRef.current.postMessage('stop'); } catch (_) {}
      try { drawWorkerRef.current.terminate(); } catch (_) {}
      drawWorkerRef.current = null;
    }
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
  }

  // ─── Save ─────────────────────────────────────────────────────

  async function saveRecording() {
    if (!recordedBlob || !authToken) {
      setError(authToken ? null : 'Not signed in. Open TrailerCast in another tab and sign in, then try again.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      const ext = recordedBlob.type.includes('mp4') ? 'mp4' : 'webm';
      const filename = `Recording ${new Date().toISOString().slice(0, 16).replace('T', ' ')}.${ext}`;
      fd.append('file', recordedBlob, filename);
      fd.append('layout', deriveLayout({
        hasCamera: !!cameraVideoElRef.current || !!cameraStreamRef.current,
        hasScreen: !!screenVideoElRef.current || !!screenStreamRef.current,
      }) || 'camera');
      fd.append('durationSeconds', String(recordedDuration));
      const r = await fetch(`${API_BASE}/api/recordings/browser`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Upload failed (${r.status})`);

      // Notify the parent tab so its trailer library refreshes.
      try {
        const bc = new BroadcastChannel(BROADCAST_CHANNEL);
        bc.postMessage({ type: 'saved', demo: body.demo, at: Date.now() });
        bc.close();
      } catch (_) { /* BroadcastChannel not supported in some Safari versions */ }

      setSavedDemo(body.demo);
      setStep('saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  function discardAndRetry() {
    setRecordedBlob(null);
    setRecordedDuration(0);
    setStep('setup');
    if (!cameraStreamRef.current) acquireCamera();
  }

  // ─── Render ────────────────────────────────────────────────────

  // No auth → tell user to sign in elsewhere first.
  if (!authToken) {
    return (
      <PageShell>
        <CenterCard>
          <h2 style={{ margin: '0 0 12px', fontSize: 22 }}>Sign in first</h2>
          <p style={{ margin: 0, color: 'rgba(248,250,252,0.7)', fontSize: 14, lineHeight: 1.6 }}>
            Open TrailerCast in another tab, sign in, then come back here. We'll know who you are once your session is live.
          </p>
        </CenterCard>
      </PageShell>
    );
  }

  if (isIOS()) {
    return (
      <PageShell>
        <CenterCard>
          <h2 style={{ margin: '0 0 12px', fontSize: 22 }}>Desktop only</h2>
          <p style={{ margin: 0, color: 'rgba(248,250,252,0.7)', fontSize: 14, lineHeight: 1.6 }}>
            Browser recording isn't supported on iOS. Open TrailerCast on a desktop browser (Chrome, Edge, Safari, or Firefox) to record. You can still upload an existing recording from this device.
          </p>
        </CenterCard>
      </PageShell>
    );
  }

  if (step === 'saved') {
    return (
      <PageShell>
        <CenterCard>
          <div style={{
            width: 64, height: 64, borderRadius: 999, margin: '0 auto 18px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 32px rgba(16, 185, 129, 0.35)',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{ margin: '0 0 12px', fontSize: 22 }}>Trailer saved</h2>
          <p style={{ margin: '0 0 18px', color: 'rgba(248,250,252,0.7)', fontSize: 14, lineHeight: 1.6 }}>
            It's in your Trailers list now. Close this tab and head back to TrailerCast — your library has already refreshed.
          </p>
          <button
            type="button"
            onClick={() => { try { window.close(); } catch (_) {} }}
            style={primaryBtnStyle}
          >
            Close this tab
          </button>
        </CenterCard>
      </PageShell>
    );
  }

  if (step === 'preview') {
    const previewUrl = recordedBlob ? URL.createObjectURL(recordedBlob) : null;
    const sizeMb = recordedBlob ? (recordedBlob.size / 1024 / 1024).toFixed(1) : '0';
    return (
      <PageShell>
        <div style={{
          maxWidth: 900, width: '100%',
          margin: '0 auto', padding: '40px 24px',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: 22, color: 'var(--room-surface, #f8fafc)' }}>Preview your recording</h2>
            <div style={{ fontSize: 12, color: 'rgba(248, 250, 252, 0.55)' }}>
              {Math.floor(recordedDuration / 60)}:{String(recordedDuration % 60).padStart(2, '0')} · {sizeMb} MB
            </div>
          </div>
          {previewUrl && (
            <video
              src={previewUrl}
              controls
              style={{ width: '100%', borderRadius: 14, background: '#000', maxHeight: '60vh' }}
            />
          )}
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <button type="button" onClick={discardAndRetry} disabled={uploading} style={secondaryBtnStyle}>
              Discard + record again
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  if (recordedBlob && !uploading) {
                    if (!window.confirm('Close this tab without saving? You\'ll lose the recording.')) return;
                  }
                  try { window.close(); } catch (_) {}
                }}
                disabled={uploading}
                style={secondaryBtnStyle}
              >
                Close without saving
              </button>
              <button
                type="button"
                onClick={saveRecording}
                disabled={uploading || !recordedBlob}
                style={uploading || !recordedBlob ? primaryBtnDisabledStyle : primaryBtnStyle}
              >
                {uploading ? 'Saving…' : 'Save as trailer'}
              </button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  if (step === 'countdown') {
    return (
      <PageShell>
        <PreviewCanvas
          cameraOn={cameraOn} screenSharing={screenSharing}
          setupCameraPreviewRef={setupCameraPreviewRef}
          setupScreenPreviewRef={setupScreenPreviewRef}
        />
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(2, 6, 23, 0.55)', pointerEvents: 'none',
        }}>
          <div
            key={countdown}
            style={{
              width: 240, height: 240, borderRadius: '50%',
              background: 'var(--room-card, #fff)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
              animation: 'tcPulseCount 1s ease-in-out',
              fontSize: 130, fontWeight: 800, color: 'var(--room-text-strong, #0f172a)', lineHeight: 1,
            }}
          >
            {countdown}
          </div>
        </div>
        <style>{`
          @keyframes tcPulseCount {
            0%   { transform: scale(0.8); opacity: 0; }
            30%  { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1.0); opacity: 1; }
          }
        `}</style>
      </PageShell>
    );
  }

  // 'setup' or 'recording' — same Tella-style layout, toolbar differs.
  return (
    <PageShell>
      <PreviewCanvas
        cameraOn={cameraOn} screenSharing={screenSharing}
        setupCameraPreviewRef={setupCameraPreviewRef}
        setupScreenPreviewRef={setupScreenPreviewRef}
      />

      {/* Top bar: brand + status */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 5,
        padding: '16px 22px',
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'linear-gradient(180deg, rgba(2,6,23,0.65) 0%, rgba(2,6,23,0) 100%)',
        pointerEvents: 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
          }} />
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'rgba(248, 250, 252, 0.75)',
          }}>
            TrailerCast Studio
          </span>
        </div>

        {step === 'recording' && (
          <div style={{
            marginLeft: 'auto',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 14px', borderRadius: 999,
            background: 'rgba(239, 68, 68, 0.18)', color: '#fca5a5',
            fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            backdropFilter: 'blur(8px)',
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: 999, background: 'currentColor',
              animation: paused ? 'none' : 'tcPulseDot 1.2s ease-in-out infinite',
            }} />
            {paused ? 'Paused' : `Recording  ·  ${formatTime(elapsedSeconds)}`}
            <style>{`@keyframes tcPulseDot { 0%,100% { opacity: 1; transform: scale(1) } 50% { opacity: 0.45; transform: scale(0.85) } }`}</style>
          </div>
        )}
      </div>

      {step === 'recording' && showSoftWarn && elapsedSeconds < HARD_CAP_SECONDS && (
        <div style={{
          position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)',
          zIndex: 5,
          padding: '6px 12px', borderRadius: 8,
          background: 'rgba(252, 211, 77, 0.18)',
          border: '1px solid rgba(252, 211, 77, 0.5)',
          color: '#fde68a', fontSize: 11.5, lineHeight: 1.4,
          backdropFilter: 'blur(8px)',
        }}>
          Getting long — recording auto-stops at 15 min.
        </div>
      )}

      {/* Bottom toolbar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 5,
        padding: '24px 22px',
        background: 'linear-gradient(0deg, rgba(2,6,23,0.85) 0%, rgba(2,6,23,0) 100%)',
        display: 'flex', alignItems: 'flex-end', gap: 12,
      }}>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ToolbarButton
            label={micOn ? 'Mute' : 'Unmute'}
            active={!micOn}
            onClick={toggleMic}
            disabled={!cameraStreamRef.current}
            icon={<MicIcon muted={!micOn} />}
          />
          <ToolbarButton
            label={cameraOn ? 'Stop camera' : 'Start camera'}
            active={!cameraOn}
            onClick={toggleCamera}
            loading={acquiringCamera}
            icon={<CameraIcon off={!cameraOn} />}
          />
          <RecordButton
            recording={step === 'recording'}
            paused={paused}
            onClick={() => {
              if (step === 'recording') stopRecording();
              else if (cameraOn || screenSharing) startRecording();
              else setError('Turn on camera or share your screen first.');
            }}
            disabled={step !== 'recording' && !cameraOn && !screenSharing}
          />
          <ToolbarButton
            label={screenSharing ? 'Stop sharing' : 'Share screen'}
            active={screenSharing}
            onClick={toggleScreen}
            loading={acquiringScreen}
            icon={<ScreenIcon active={screenSharing} />}
          />
          {step === 'recording' && (
            <ToolbarButton
              label={paused ? 'Resume' : 'Pause'}
              onClick={paused ? resumeRecording : pauseRecording}
              icon={paused ? <PlayIcon /> : <PauseIcon />}
            />
          )}
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {error && (
            <div style={{
              maxWidth: 320,
              padding: '8px 12px', borderRadius: 8,
              background: 'rgba(239, 68, 68, 0.18)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#fca5a5', fontSize: 11.5, lineHeight: 1.45,
              backdropFilter: 'blur(8px)',
            }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}

// ─── Sub-components ───────────────────────────────────────────────

function PageShell({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#020617',
      color: 'var(--room-surface, #f8fafc)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Helvetica, Arial, sans-serif',
      overflow: 'hidden',
    }}>
      {children}
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        maxWidth: 480, width: '100%',
        padding: 32, borderRadius: 16,
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.6) 100%)',
        border: '1px solid rgba(248, 250, 252, 0.08)',
        textAlign: 'center',
        backdropFilter: 'blur(20px)',
      }}>
        {children}
      </div>
    </div>
  );
}

function PreviewCanvas({ cameraOn, screenSharing, setupCameraPreviewRef, setupScreenPreviewRef }) {
  const layout = deriveLayout({ hasCamera: cameraOn, hasScreen: screenSharing });
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background:
        'radial-gradient(80% 60% at 50% 0%, rgba(124, 58, 237, 0.12) 0%, rgba(124, 58, 237, 0) 60%), #020617',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
    }}>
      {!layout && (
        <div style={{
          textAlign: 'center', color: 'rgba(248,250,252,0.55)',
          padding: 32, fontSize: 14, lineHeight: 1.6, maxWidth: 480,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 18,
            margin: '0 auto 22px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            opacity: 0.45,
            boxShadow: '0 12px 32px rgba(124, 58, 237, 0.3)',
          }} />
          <div style={{ color: 'var(--room-surface, #f8fafc)', fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
            Ready when you are
          </div>
          <div>
            Turn on your camera below or click <strong>Share screen</strong> to choose a window or tab to capture.
          </div>
        </div>
      )}

      {layout === 'camera' && (
        <video
          ref={setupCameraPreviewRef}
          autoPlay muted playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
        />
      )}

      {layout === 'screen' && (
        <video
          ref={setupScreenPreviewRef}
          autoPlay muted playsInline
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      )}

      {layout === 'screen_camera_pip' && (
        <>
          <video
            ref={setupScreenPreviewRef}
            autoPlay muted playsInline
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
          <video
            ref={setupCameraPreviewRef}
            autoPlay muted playsInline
            style={{
              position: 'absolute', right: 32, bottom: 120,
              width: 200, height: 200, objectFit: 'cover',
              borderRadius: '50%',
              border: '4px solid #fff',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              transform: 'scaleX(-1)',
            }}
          />
        </>
      )}
    </div>
  );
}

function ToolbarButton({ label, icon, active, onClick, loading, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        padding: '12px 16px', minWidth: 84,
        borderRadius: 12,
        border: '1px solid ' + (active ? 'rgba(239, 68, 68, 0.5)' : 'rgba(248, 250, 252, 0.12)'),
        background: active ? 'rgba(239, 68, 68, 0.16)' : 'rgba(15, 23, 42, 0.55)',
        color: active ? '#fca5a5' : 'var(--room-surface, #f8fafc)',
        cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        backdropFilter: 'blur(10px)',
        transition: 'background 0.14s ease, border-color 0.14s ease, transform 0.14s ease',
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled && !loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? <Spinner /> : icon}
      </div>
      <span style={{ fontSize: 11, fontWeight: 500 }}>{label}</span>
    </button>
  );
}

function RecordButton({ recording, paused, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={recording ? 'Stop recording' : 'Start recording'}
      style={{
        width: 78, height: 78, borderRadius: '50%',
        border: '4px solid #f8fafc',
        background: 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)',
        color: '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: recording
          ? '0 8px 24px rgba(220, 38, 38, 0.45), 0 0 0 6px rgba(239, 68, 68, 0.15)'
          : '0 6px 20px rgba(220, 38, 38, 0.35)',
        transition: 'all 0.14s ease',
        opacity: disabled ? 0.55 : 1,
        margin: '0 4px',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(1.05)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {recording ? (
        <div style={{ width: 24, height: 24, background: 'var(--room-card, #fff)', borderRadius: 4 }} />
      ) : (
        <div style={{ width: 28, height: 28, background: 'var(--room-card, #fff)', borderRadius: '50%' }} />
      )}
    </button>
  );
}

function ErrorBanner({ children }) {
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 10,
      background: 'rgba(239, 68, 68, 0.15)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      color: '#fca5a5', fontSize: 13, lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

// ─── Helpers + icons ──────────────────────────────────────────────

function formatTime(secs) {
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function MicIcon({ muted }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
      {muted && <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2.5" />}
    </svg>
  );
}
function CameraIcon({ off }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      {off && <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2.5" />}
    </svg>
  );
}
function ScreenIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      {active && <circle cx="12" cy="10" r="3" fill="currentColor" />}
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4l14 8-14 8V4z" />
    </svg>
  );
}
function Spinner() {
  return (
    <div style={{
      width: 16, height: 16,
      border: '2px solid rgba(255,255,255,0.2)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'tcSpin 0.8s linear infinite',
    }}>
      <style>{`@keyframes tcSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Button styles ────────────────────────────────────────────────

const primaryBtnStyle = {
  padding: '10px 18px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%)',
  color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
  transition: 'filter 0.14s ease',
};
const primaryBtnDisabledStyle = { ...primaryBtnStyle, opacity: 0.55, cursor: 'not-allowed' };
const secondaryBtnStyle = {
  padding: '10px 16px', borderRadius: 10,
  background: 'rgba(248, 250, 252, 0.06)',
  color: 'var(--room-border-soft, #f1f5f9)',
  border: '1px solid rgba(248, 250, 252, 0.12)',
  fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
};
